import { type FormEvent, useState } from "react";

import AppShell, { EmptyState, ErrorNote } from "@/components/AppShell";
import NumericInput from "@/components/NumericInput";
import { api } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import type { PumpCapabilities, PumpSchedule, PumpWindow } from "@/types";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface DraftWindow {
  weekday: number;
  start_time: string;
  end_time: string;
  rpm: string;
}

export default function Pump() {
  const capabilities = useAsync<PumpCapabilities>(
    () => api.get<PumpCapabilities>("/api/pump/active-pump-capabilities"),
    [],
  );
  const schedules = useAsync<PumpSchedule[]>(
    () => api.get<PumpSchedule[]>("/api/pump/schedules"),
    [],
  );
  const [editing, setEditing] = useState(false);

  const current = (schedules.data ?? []).find((s) => s.is_current);
  const previous = (schedules.data ?? []).filter((s) => !s.is_current);

  return (
    <AppShell
      title="Pump schedule"
      subtitle={capabilities.data?.pump_label ?? undefined}
      action={
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="btn btn-primary px-3 py-2 text-sm"
        >
          New
        </button>
      }
    >
      {capabilities.data?.note && (
        <p className="mb-4 text-xs text-(--color-ink-faint)">{capabilities.data.note}</p>
      )}

      {schedules.error && <ErrorNote message={schedules.error} />}

      {schedules.data && schedules.data.length === 0 && (
        <EmptyState
          title="No schedule recorded"
          body="Recording it means a chart from last July can tell you what the pump was doing at the time."
          action={
            <button type="button" onClick={() => setEditing(true)} className="btn btn-primary">
              Record the schedule
            </button>
          }
        />
      )}

      {current && <ScheduleCard schedule={current} />}

      {previous.length > 0 && (
        <section className="mt-6">
          <h2 className="unit mb-2">Previous schedules</h2>
          <ul className="space-y-2">
            {previous.map((schedule) => (
              <li key={schedule.id}>
                <ScheduleCard schedule={schedule} />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-(--color-ink-faint)">
            Old schedules are kept so a past chart can be read against what the pump was actually
            doing.
          </p>
        </section>
      )}

      {editing && capabilities.data && (
        <ScheduleDialog
          capabilities={capabilities.data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            schedules.reload();
          }}
        />
      )}
    </AppShell>
  );
}

function ScheduleCard({ schedule }: { schedule: PumpSchedule }) {
  const byDay = new Map<number, PumpWindow[]>();
  for (const window of schedule.windows) {
    byDay.set(window.weekday, [...(byDay.get(window.weekday) ?? []), window]);
  }

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-medium">{schedule.label ?? "Schedule"}</p>
        <p className="unit">{Number(schedule.weekly_hours).toFixed(0)} hrs/week</p>
      </div>
      <p className="unit mt-0.5">
        From {schedule.effective_from}
        {schedule.effective_to ? ` to ${schedule.effective_to}` : " — current"}
      </p>

      <ul className="mt-3 space-y-1 text-sm">
        {DAYS.map((day, index) => {
          const windows = byDay.get(index) ?? [];
          return (
            <li key={day} className="flex gap-3">
              <span className="w-10 text-(--color-ink-muted)">{day}</span>
              <span className="figure">
                {windows.length === 0
                  ? "—"
                  : windows
                      .map(
                        (w) =>
                          `${w.start_time.slice(0, 5)}–${w.end_time.slice(0, 5)}` +
                          (w.rpm ? ` @ ${w.rpm}` : ""),
                      )
                      .join(", ")}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ScheduleDialog({
  capabilities,
  onClose,
  onSaved,
}: {
  capabilities: PumpCapabilities;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [windows, setWindows] = useState<DraftWindow[]>(
    DAYS.map((_, index) => ({ weekday: index, start_time: "09:00", end_time: "17:00", rpm: "" })),
  );
  const [enabled, setEnabled] = useState<boolean[]>(DAYS.map(() => true));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update(index: number, patch: Partial<DraftWindow>) {
    setWindows((prev) => prev.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const payload = windows
      .filter((_, index) => enabled[index])
      .map((w) => ({
        weekday: w.weekday,
        start_time: w.start_time,
        end_time: w.end_time,
        // RPM is only sent when the installed pump is variable-speed — the API
        // rejects it otherwise, which is the point of asking the pump first.
        ...(capabilities.variable_speed && w.rpm ? { rpm: Number(w.rpm) } : {}),
      }));

    if (payload.length === 0) {
      setError("Turn on at least one day.");
      setBusy(false);
      return;
    }

    try {
      await api.post("/api/pump/schedules", {
        effective_from: effectiveFrom,
        label: label || null,
        windows: payload,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that schedule.");
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New pump schedule"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-(--color-surface-raised) p-5 sm:rounded-2xl"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">New pump schedule</h2>
          <button type="button" onClick={onClose} className="text-sm text-(--color-ink-muted)">
            Close
          </button>
        </div>

        <p className="mt-1 text-xs text-(--color-ink-faint)">
          This opens a new schedule and closes the current one, so the old one stays readable
          against past charts.
        </p>

        <div className="mt-4 space-y-3">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label — e.g. Summer 2026"
            className="field"
          />
          <div>
            <label htmlFor="effective" className="text-sm font-medium">
              In force from
            </label>
            <input
              id="effective"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="field mt-1.5"
            />
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {DAYS.map((day, index) => (
            <div key={day} className="flex items-center gap-2">
              <label className="flex w-16 shrink-0 items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={enabled[index]}
                  onChange={(e) =>
                    setEnabled((prev) => prev.map((v, i) => (i === index ? e.target.checked : v)))
                  }
                  className="size-4"
                />
                {day}
              </label>
              <input
                type="time"
                value={windows[index].start_time}
                disabled={!enabled[index]}
                onChange={(e) => update(index, { start_time: e.target.value })}
                className="field figure flex-1 disabled:opacity-40"
              />
              <input
                type="time"
                value={windows[index].end_time}
                disabled={!enabled[index]}
                onChange={(e) => update(index, { end_time: e.target.value })}
                className="field figure flex-1 disabled:opacity-40"
              />
              {/* Only rendered when the installed pump can actually do it. */}
              {capabilities.variable_speed && (
                <NumericInput
                  decimal={false}
                  placeholder="RPM"
                  value={windows[index].rpm}
                  disabled={!enabled[index]}
                  onChange={(next) => update(index, { rpm: next })}
                  className="field figure w-20 disabled:opacity-40"
                />
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNote message={error} />
          </div>
        )}

        <button type="submit" disabled={busy} className="btn btn-primary mt-5 w-full">
          {busy ? "Saving…" : "Save schedule"}
        </button>
      </form>
    </div>
  );
}
